<template>
  <div class="flex flex-col">
    <div class="text-black text-2xl font-medium mb-3">
      Check Balance & Transaction
    </div>
    <Widget />
    <BCard>
      <div class="font-semibold text-black">
        Ticket Issue
      </div>

      <div class="mt-[32px] flex gap-[20px] border-b-[1px] border-[#f4f4f4]">
        <div
          v-for="tab in tabs"
          :key="tab.key"
          :class="activeTab === tab.key && 'border-b-[2px] text-[#F95031] border-[#F95031]'"
          class="flex gap-[4px] items-center pb-[8px] cursor-pointer"
          @click="handleActiveTab(tab.key)"
        >
          <span
            :class="tab.icon"
            class="text-[16px]"
          />
          <div>{{ tab.label }}</div>
        </div>
      </div>

      <div class="flex flex-wrap gap-[12px] mt-1">
        <div class="lg:w-1/4 w-full">
          <b-input-group
            class="input-group-merge"
            size="md"
          >
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="filterInput"
              v-model="search"
              :maxlength="32"
              :minlength="2"
              type="search"
              placeholder="Search 4 last digits"
              debounce="500"
              @input="onChangeSearch"
            />
          </b-input-group>
        </div>
        <date-range-picker
          ref="picker"
          v-model="dateRange"
          :locale-data="locale"
          :show-ranges="false"
          :ranges="false"
          :opens="'auto'"
          control-container-class="border rounded"
        >
          <template v-slot:input="picker">
            <div
              class="d-flex btn justify-content-between align-items-center gap-5"
              style="height: 32px;"
            >
              <span
                v-if="!isDateChanged"
                class="d-flex justify-content-start text-[#A8A8A8] font-normal"
              >
                Start date &nbsp; ⇀ &nbsp; End date
              </span>
              <span
                v-else
                class="d-flex justify-content-start text-black font-medium"
              >
                {{ YEAR_MONTH_DAY(picker.startDate) }} ⇀ {{ YEAR_MONTH_DAY(picker.endDate) }}
              </span>
              <img
                src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/calendar.svg"
                alt="calendar"
                width="15px"
                class="d-flex justify-content-end"
              >
            </div>
          </template>
        </date-range-picker>
        <div
          v-if="activeTab === 'new_issue'"
          class="w-fit h-[32px] ml-1 pl-1 border-l-[2px] border-light-gray"
        >
          <BButton
            :variant="hasSelection ? 'primary' : 'flat'"
            :disabled="!hasSelection"
            @click="openModalConfirm"
          >
            <div class="flex gap-[4px] items-center">
              <div>Move to On Process</div>
              <span class="k-arrow-right-2" />
            </div>
          </BButton>
        </div>
      </div>

      <BOverlay
        :show="loadingTable"
        spinner-variant="primary"
        variant="light"
        blur="0"
        opacity=".5"
        rounded="sm"
      >
        <div
          id="table"
          class="mt-1"
          style="height: calc(100vh - 250px); overflow-y: scroll;"
        >
          <BTable
            :items="items"
            :fields="fields"
            show-empty
            empty-text="Tidak ada data"
            hover
            selectable
            select-mode="single"
          >
            <template
              v-if="activeTab === 'new_issue'"
              #head(checkbox)
            >
              <div class="d-flex justify-content-center">
                <BCheckbox
                  v-model="allSelected"
                  :indeterminate="indeterminate"
                  @change="selectAll"
                />
              </div>
            </template>
            <template
              v-if="activeTab === 'new_issue'"
              #cell(checkbox)="data"
            >
              <div class="d-flex justify-content-center">
                <BCheckbox
                  v-model="selected"
                  :value="data.item"
                  @change="select()"
                />
              </div>
            </template>
            <template #cell(date)="data">
              <div>
                {{ DAY_MONTH_YEAR(data.item.date) }}
              </div>
              <div class="text-black text-[12px]">
                {{ moment(data.item.date).format('HH:mm:ss') }}
              </div>
            </template>
            <template #cell(name)="data">
              <div class="flex flex-col">
                <div class="font-medium text-secondary text-nowrap">
                  {{ data.item.user.name }}
                </div>
                <div class="text-black text-[12px]">
                  {{ data.item.user.email }}
                </div>
              </div>
            </template>
            <template #cell(card_uuid)="data">
              <div class="flex flex-col">
                <div class="font-medium flex items-center gap-[4px]">
                  <i class="k-card text-[20px]" />
                  <span class="text-nowrap">
                    {{ data.item.card.name }} ***{{ data.item.card.last_number }}
                  </span>
                  <BButton
                    variant="flat"
                    class="p-0 w-[25px] h-[25px] flex items-center justify-center"
                    @click="copyCardUuid(data.item.card.card_uuid)"
                  >
                    <img
                      src="https://storage.googleapis.com/komerce/icon/ie7/copy.svg"
                      alt="copy"
                      class="w-[20px]"
                    >
                  </BButton>
                </div>
              </div>
            </template>
            <template #cell(monit_balance)="data">
              <div class="text-[14px] text-[#333333] text-left flex justify-start w-100">
                {{ IDR(data.item.monit_balance || 0) }}
              </div>
            </template>
            <template #cell(komcard_balance)="data">
              <div class="text-[14px] text-[#333333] text-left flex justify-start w-100">
                {{ IDR(data.item.komcard_balance || 0) }}
              </div>
            </template>
            <template #cell(type_ticket)="data">
              <div
                v-if="data.item.status !== 'solved'"
                class="flex items-center"
              >
                <div class="flex flex-col gap-[6px] items-start">
                  <span
                    v-for="(ticket, idx) in data.item.type_ticket"
                    :key="idx"
                    :class="getClassTicket(ticket)"
                    class="px-[12px] py-[2px] rounded text-[13px] font-semibold text-center inline-block text-nowrap w-[160px]"
                  >
                    {{ ticket }}
                  </span>
                </div>
                <BButton
                  variant="flat"
                  class="p-0 w-[25px] h-[25px] flex items-center justify-center"
                  @click="openDetail(data.item)"
                >
                  <i class="k-arrow-right-2 text-[20px]" />
                </BButton>
              </div>
              <div
                v-else
                class="flex items-center justify-center bg-[#dcf2eb] text-[#589b80] rounded-sm px-[5px] py-[4px] text-center"
              >
                Done
              </div>
            </template>
          </BTable>
        </div>
      </BOverlay>

      <BRow class="mt-1">
        <BCol
          cols="12"
          class="flex flex-col md:flex-row justify-between bg-[#fafafa] items-center rounded p-1 gap-[12px] md:gap-0"
        >
          <div class="flex flex-wrap items-center justify-center md:justify-start gap-[12px]">
            <span class="text-black">{{ `Total ${totalRows} Data` }}</span>
            <div class="bg-light d-flex justify-content-center align-items-center p-50 rounded">
              <span class="text-black mr-50">List per page: </span>
              <BButton
                v-for="page in optionsPage"
                :key="page"
                class="btn-icon"
                size="sm"
                :variant="totalPerPage === page ? 'primary' : 'flat-dark'"
                @click="setPerPage(page)"
              >
                {{ page }}
              </BButton>
            </div>
          </div>

          <BPagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="totalPerPage"
            first-number
            last-number
            class="pagination-primary mb-0"
          >
            <template #prev-text>
              <feather-icon
                size="18"
                icon="ChevronLeftIcon"
              />
            </template>
            <template #next-text>
              <feather-icon
                size="18"
                icon="ChevronRightIcon"
              />
            </template>
          </BPagination>
        </BCol>
      </BRow>
    </BCard>
    <DetailIssueDrawer
      :visible="visibleDetailDrawer"
      :issue="selectedIssue"
      @close="visibleDetailDrawer = false"
      @move-on-process="moveOnProcessFromDrawer"
    />
    <ModalConfirmOnProcess
      :visible="visibleModalConfirm"
      @close="visibleModalConfirm = false"
      @confirm="moveOnProcess"
    />
  </div>
</template>

<script>
import {
  LABELDAY,
  LABELMONTH,
  DAY_MONTH_YEAR,
  YEAR_MONTH_DAY,
} from '@/libs/filterDate'
import moment from 'moment'
import { newAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import {
  today,
  komcardsDate,
} from '@/store/helpers'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import Widget from './Widget.vue'
import {
  columnTabelConfig,
  tabsConfig,
  ticketClassMap,
  TICKET_CLASS_DEFAULT,
  OPTIONS_PAGE,
} from './config'
import DetailIssueDrawer from './DetailIssueDrawer.vue'
import ModalConfirmOnProcess from './ModalConfirmOnProcess.vue'

export default {
  name: 'ListCheckBalanceTransaction',
  components: {
    Widget,
    DateRangePicker,
    DetailIssueDrawer,
    ModalConfirmOnProcess,
  },
  data() {
    return {
      IDR,
      DAY_MONTH_YEAR,
      YEAR_MONTH_DAY,
      moment,
      tabs: tabsConfig,
      activeTab: 'new_issue',
      loadingTable: false,
      items: [],
      fields: columnTabelConfig,
      search: '',
      locale: {
        format: 'dd/mm/yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
      },
      dateRange: {
        startDate: komcardsDate,
        endDate: today,
      },
      selected: [],
      allSelected: false,
      indeterminate: false,
      currentPage: 1,
      optionsPage: OPTIONS_PAGE,
      totalPerPage: 25,
      totalRows: 0,
      selectedIssue: null,
      visibleDetailDrawer: false,
      visibleModalConfirm: false,
      isDateChanged: false,
    }
  },
  computed: {
    hasSelection() {
      return this.allSelected || this.selected.length > 0
    },
  },
  watch: {
    selected(newValue) {
      this.indeterminate = newValue.length > 0 && newValue.length < this.items.length
    },
    dateRange() {
      this.isDateChanged = true
      this.currentPage = 1
      this.getTicketList()
    },
    currentPage() {
      this.getTicketList()
    },
  },
  mounted() {
    this.getTicketList()
  },
  methods: {
    handleActiveTab(tab) {
      this.activeTab = tab
      this.currentPage = 1
      this.selected = []
      this.allSelected = false
      this.getTicketList()
    },
    onChangeSearch: _.debounce(function search() {
      const trimmedSearch = this.search.trim()
      this.search = trimmedSearch
      this.currentPage = 1
      this.getTicketList()
    }, 1000),
    selectAll(checked) {
      this.selected = checked ? this.items.slice() : []
    },
    select() {
      this.allSelected = false
    },
    copyCardUuid(uuid) {
      navigator.clipboard.writeText(uuid).then(() => {
        this.$toast_success({ message: `${uuid} Copied` })
      })
    },
    maskCardUuid(uuid) {
      if (!uuid) return ''
      if (uuid.length <= 6) return uuid
      return `${uuid.substring(0, 3)}***${uuid.substring(uuid.length - 3)}`
    },
    setPerPage(page) {
      this.totalPerPage = page
      this.currentPage = 1
      this.getTicketList()
    },
    getClassTicket(ticket) {
      return ticketClassMap[ticket] || TICKET_CLASS_DEFAULT
    },
    openDetail(item) {
      this.selectedIssue = item
      this.visibleDetailDrawer = true
    },
    openModalConfirm() {
      this.visibleModalConfirm = true
    },
    moveOnProcessFromDrawer() {
      this.selected = [this.selectedIssue]
      this.moveOnProcess()
    },
    async getTicketList() {
      this.loadingTable = true
      const params = {
        status: this.activeTab,
        start_date: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
        end_date: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
        search: this.search,
        page: this.currentPage,
        per_page: this.totalPerPage,
      }
      const url = 'komcards/api/v1/tickets'

      await newAxiosIns
        .get(url, { params })
        .then(res => {
          this.items = res.data.data
          this.totalRows = res.data.meta.pagination.total_row
        })
        .catch(err => {
          this.$toast_error({ message: err.response.data.message })
        })
        .finally(() => {
          this.loadingTable = false
        })
    },
    async moveOnProcess() {
      const url = 'komcards/api/v1/admin/ticket/on-process'
      const params = {
        id: this.selected.map(item => item.id),
      }

      await newAxiosIns
        .put(url, params)
        .then(res => {
          this.$toast_success({ message: 'Data has been moved to On Process' })
          this.visibleModalConfirm = false
          this.visibleDetailDrawer = false
          this.selected = []
          this.allSelected = false
          this.getTicketList()
        })
        .catch(err => {
          this.$toast_error({ message: err.response.data.message })
        })
    },
  },
}
</script>
